/**
 * Shared hook for resolving a style ID to its display label and
 * computing the total public style count.
 *
 * Every surface that shows a style name (landing pages, project config gear,
 * config tab) should use this hook instead of ad-hoc lookups against the
 * hardcoded VISUAL_STYLES array or individual API queries.
 *
 * Resolution chain (first non-null wins):
 *   1. User styles API (`GET /styles/user`) — custom styles take priority
 *   2. Public styles API (`GET /styles?ui_locale=...`) — server-localized
 *   3. Hardcoded VISUAL_STYLES fallback (placeholder while API loads)
 *   4. Caller-supplied `modalSelectionName` (style name captured at modal
 *      selection time, before API data refreshes)
 *   5. `"Style"` default
 *
 * Feature tag: `dw:style-creator-preview-name`
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useStyles, useUserStylesQuery } from "../api/hooks";
import { VISUAL_STYLES } from "../constants/styles";
import { localizeStyleName } from "../i18n/styleLabels";

type StyleLabelSource =
  | "public-styles-api"
  | "user-styles-query"
  | "static-fallback"
  | "modal-selection"
  | "fallback";

export interface StyleLabelResult {
  /** Resolved display name for the style. */
  label: string;
  /** Total number of public styles (API count, falling back to hardcoded count). */
  count: number;
  /** Which source provided the label — useful for NAD trace logging. */
  source: StyleLabelSource;
  /**
   * Thumbnail for the resolved style. API-backed styles use
   * `StyleSummary.thumbnail_url`, then fall back to the first
   * `preview_image_urls` entry. Local `VISUAL_STYLES` fallback records and
   * caller-supplied modal selections have no image and resolve to `null`.
   *
   * Still a raw API value — callers render it through
   * `getStyleImageUrl()` (see `api/styles.ts`), which passes signed URLs
   * through and routes relative paths at the API proxy. An API record marked
   * `is_static_fallback` can still provide API imagery; only the local
   * hardcoded fallback is image-free.
   */
  thumbnailUrl: string | null;
}

function result(
  label: string,
  count: number,
  source: StyleLabelSource,
  thumbnailUrl: string | null = null,
): StyleLabelResult {
  return { label, count, source, thumbnailUrl };
}

/**
 * Resolve a style ID to its display label and compute the public style count.
 *
 * @param styleId — The style ID to resolve (e.g. `"bold-manhwa"`)
 * @param modalSelectionName — Optional name captured when the user picks a
 *   style from the modal, before the API data refreshes.
 */
export function useStyleLabel(
  styleId: string | undefined,
  modalSelectionName?: string,
): StyleLabelResult {
  const { data: publicStylesData } = useStyles(true);
  const { data: userStylesData } = useUserStylesQuery();
  // Re-resolve built-in labels when the UI locale changes: react-i18next returns
  // a NEW `t` identity on every `languageChanged`, so listing `t` in the useMemo
  // deps below re-runs resolution on a locale switch. (Adding `i18n.language`
  // too is redundant with `t` and react-hooks/exhaustive-deps flags it.)
  const { t } = useTranslation("shared");

  return useMemo(() => {
    const count =
      publicStylesData?.styles.length ?? VISUAL_STYLES.length;
    const id = styleId?.trim();

    if (!id) {
      return result("Style", count, "fallback");
    }

    // User (custom) styles take priority AND are discriminated by SOURCE, not
    // by id. A user can create a style whose id collides with a built-in slug
    // (e.g. naming it "Bold Manhwa" → id "bold-manhwa"; the backend dup check is
    // scoped to the user, not the `_public` catalog). Because this branch is
    // checked first and NEVER calls localizeStyleName, that user's own name is
    // always returned verbatim — user content is never auto-translated, even on
    // an id collision. Only the static fallback below calls localizeStyleName.
    const userMatch = userStylesData?.styles.find((s) => s.id === id);
    if (userMatch?.name) {
      return result(
        userMatch.name,
        count,
        "user-styles-query",
        userMatch.thumbnail_url ?? userMatch.preview_image_urls?.[0] ?? null,
      );
    }

    // DB-backed public styles are localized server-side by `ui_locale`. The
    // React Query placeholder/error path is explicitly marked as static fallback
    // and still uses frontend styleLabels.
    const publicMatch = publicStylesData?.styles.find((s) => s.id === id);
    if (publicMatch) {
      if (publicMatch.is_static_fallback) {
        return result(
          localizeStyleName(id, publicMatch.name, t),
          count,
          "static-fallback",
          publicMatch.thumbnail_url ?? publicMatch.preview_image_urls?.[0] ?? null,
        );
      }
      return result(
        publicMatch.name,
        count,
        "public-styles-api",
        publicMatch.thumbnail_url ?? publicMatch.preview_image_urls?.[0] ?? null,
      );
    }

    const staticMatch = VISUAL_STYLES.find((s) => s.value === id)?.label;
    if (staticMatch) {
      return result(localizeStyleName(id, staticMatch, t), count, "static-fallback");
    }

    if (modalSelectionName) {
      return result(modalSelectionName, count, "modal-selection");
    }

    return result("Style", count, "fallback");
  }, [styleId, publicStylesData, userStylesData, modalSelectionName, t]);
}
